/**
 * Sentry custom-span helpers.
 *
 * Spans created here attach to the active pageload/navigation transaction and
 * inherit its sampling decision (no extra config). Use a dot-namespaced `op`
 * so spans group cleanly in Explore > Traces (e.g. `app.init.*`, `page.init.*`).
 */
import * as Sentry from '@sentry/sveltekit';

/**
 * Run `fn` inside a Sentry span and return its result.
 * The span ends when `fn` settles. Synchronous and async `fn` are both supported.
 * @template T
 * @param {string} op - Dot-namespaced operation, e.g. `app.init.experiments`.
 * @param {string} name - Human-readable span name.
 * @param {(span: import('@sentry/core').Span | undefined) => T} fn - Work to measure; receives the span so it can set attributes.
 * @param {Record<string, string | number | boolean>} [attributes] - Attributes known up front.
 * @returns {T}
 */
export function instrument(op, name, fn, attributes) {
	// Defensive: if tracing is unavailable (e.g. partially-mocked Sentry in
	// tests), run the work directly so instrumentation never alters behavior.
	if (typeof Sentry.startSpan !== 'function') {
		return fn(undefined);
	}
	return Sentry.startSpan({ name, op, attributes }, fn);
}

/**
 * Record that `page.init()` resolved the variant component. Emits an
 * `abc.variant_ready` User Timing mark and a Sentry `time_to_variant_ready`
 * measurement (ms since timeOrigin). This is a **JS-resolution** timestamp:
 * it fires synchronously after the component class is available, before Svelte
 * commits the variant DOM and before the variant is painted. Do not interpret
 * it as "usable page painted" — use `markVariantPainted` for that.
 */
export function markVariantReady() {
	userTimingMark('abc.variant_ready');
	sentryMeasurement('time_to_variant_ready');
}

/**
 * Record that the prerendered skeleton became visible. Emits an
 * `abc.skeleton_visible` User Timing mark and a Sentry `time_to_skeleton_visible`
 * measurement. Schedule this via `afterPaint` from the skeleton's `onMount` so
 * the mark lands after a paint boundary rather than at assignment time.
 */
export function markSkeletonVisible() {
	userTimingMark('abc.skeleton_visible');
	sentryMeasurement('time_to_skeleton_visible');
}

/**
 * Record that the resolved variant has been painted. Emits an `abc.variant_painted`
 * User Timing mark and a Sentry `time_to_variant_painted` measurement. Call this
 * after `markVariantReady`, a Svelte `tick()` (DOM commit), and an `afterPaint`
 * paint boundary so the timestamp reflects a painted variant, not just resolution.
 */
export function markVariantPainted() {
	userTimingMark('abc.variant_painted');
	sentryMeasurement('time_to_variant_painted');
}

/**
 * Run `fn` after the next two animation frames so it executes after the browser
 * has had a chance to paint the current DOM. Returns a cancel function so callers
 * (e.g. a skeleton that unmounts before painting) can abort a pending callback.
 * In non-browser/test environments without `requestAnimationFrame`, runs `fn` on
 * the next microtask so instrumentation stays async-safe without breaking tests.
 * @param {() => void} fn - Work to run after a paint boundary.
 * @returns {() => void}
 */
export function afterPaint(fn) {
	if (typeof requestAnimationFrame !== 'function') {
		let cancelled = false;
		Promise.resolve().then(() => {
			if (!cancelled) fn();
		});
		return () => {
			cancelled = true;
		};
	}
	let raf1 = 0;
	let raf2 = 0;
	let cancelled = false;
	raf1 = requestAnimationFrame(() => {
		raf2 = requestAnimationFrame(() => {
			if (!cancelled) fn();
		});
	});
	return () => {
		cancelled = true;
		if (raf1) cancelAnimationFrame(raf1);
		if (raf2) cancelAnimationFrame(raf2);
	};
}

/** @param {string} name - User Timing mark name. */
function userTimingMark(name) {
	if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
		try {
			performance.mark(name);
		} catch {
			/* ignore — mark API must not break rendering */
		}
	}
}

/** @param {string} name - Sentry measurement name. */
function sentryMeasurement(name) {
	if (typeof Sentry.setMeasurement === 'function') {
		try {
			const ms =
				typeof performance !== 'undefined' && typeof performance.now === 'function'
					? performance.now()
					: 0;
			Sentry.setMeasurement(name, ms, 'millisecond');
		} catch {
			/* ignore — measurement must not break rendering */
		}
	}
}
